/**
 * Success tuple type with data and null error
 */
type TryCatchSuccess<T> = readonly [T, null]

/**
 * Failure tuple type with null data and error
 */
type TryCatchFailure<E> = readonly [null, E]

/**
 * Result type combining success and failure cases
 */
type TryCatchResult<T, E> = TryCatchSuccess<T> | TryCatchFailure<E>

/**
 * Wraps an operation in a try-catch block and returns a tuple of [data, error]
 * @example
 * // Async/Promise usage:
 * const [data, error] = await tryCatch(async () => {
 *   const response = await fetch('https://api.example.com/data')
 *   return response.json()
 * })
 *
 * // Direct Promise usage:
 * const [data, error] = await tryCatch(fetch('https://api.example.com/data'))
 *
 * // Sync usage:
 * const [data, error] = tryCatch(() => JSON.parse(jsonString))
 */
export async function tryCatch<T, E = Error>(promise: Promise<T>): Promise<TryCatchResult<T, E>>
export async function tryCatch<T, E = Error>(fn: () => Promise<T>): Promise<TryCatchResult<T, E>>
export function tryCatch<T, E = Error>(fn: () => T): TryCatchResult<T, E>
export function tryCatch<T, E = Error>(
  operation: Promise<T> | (() => T | Promise<T>),
): TryCatchResult<T, E> | Promise<TryCatchResult<T, E>> {
  // Handle direct Promise
  if (operation instanceof Promise) {
    return operation
      .then((data: T) => [data, null] as const)
      .catch((error: unknown) => {
        console.error(error)
        return [null, error as E] as const
      })
  }

  try {
    const result = operation()

    // Handle async function
    if (result instanceof Promise) {
      return result
        .then((data: T) => [data, null] as const)
        .catch((error: unknown) => {
          console.error(error)
          return [null, error as E] as const
        })
    }

    // Handle sync function
    return [result, null] as const
  } catch (error: unknown) {
    console.error(error)
    return [null, error as E] as const
  }
}
